// frontend/src/utils/carCategory.js
//
// The browser's doorway to the one category module. There is no detection logic here
// any more.
//
// There used to be a second, near-identical copy of the whole detector in this file,
// because the shared module runs on the backend too and could not import apiRequest.
// A test kept the two keyword tables in step, which sounded like enough and was not:
// only this copy read the admin config, so changing a keyword re-categorised a car on
// screen while the server went on pricing it the old way. Two implementations that
// agree on their constants can still disagree on their behaviour.
//
// The shared module now takes the config as an argument instead of fetching it. This
// file's whole job is to fetch it in the way only a browser can and hand it over.
// Every existing import site keeps working because everything is re-exported.

import { apiRequest } from "./api";
import { setCategoryConfig, notifyCategoryConfigChanged } from "../shared/pricing/carCategory";
import { setCustomCategories, setCategoryOrder, setCategorySeats, setCategoryLabels } from "../shared/pricing/constants";

export {
  detectCarCategory,
  mapLegacyCategory,
  formatCarName,
  detectSeats,
  resolveShortName,
  resolveSeatsFor,
  onCategoryConfigChange,
  setCategoryConfig,
  getCategoryConfig,
  CATEGORY_COLORS,
  DISPLAY_LABELS,
  HARDCODED_KEYWORDS,
} from "../shared/pricing/carCategory";

let _lastFetched = 0;
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Pull the admin's category configuration and install it into the shared module.
 *
 * Best effort by design. A failure leaves whatever was last installed — or the
 * built-in tables on a cold start — so the app categorises cars either way rather than
 * blocking on a settings fetch.
 */
export async function loadCategoryConfig({ force = false } = {}) {
  if (!force && _lastFetched && Date.now() - _lastFetched < CACHE_TTL_MS) return;
  try {
    const res = await apiRequest("/config/categories", { method: "GET" });
    const data = res?.data || {};
    if (!data.keywords && !data.modelOverrides && !data.shortNames
        && !data.seatOverrides && !data.customCategories && !data.categoryOrder
        && !data.categorySeats && !data.categoryLabels) return;

    // Keyword arrays are sorted longest-first so a specific phrase is tested before a
    // generic word that happens to be its prefix — "innova crysta" before "innova".
    const keywords = {};
    for (const [key, list] of Object.entries(data.keywords || {})) {
      if (Array.isArray(list)) keywords[key] = [...list].sort((a, b) => b.length - a.length);
    }

    setCategoryConfig({
      keywords: Object.keys(keywords).length ? keywords : undefined,
      modelOverrides: data.modelOverrides,
      shortNames: data.shortNames,
      seatOverrides: data.seatOverrides,
    });
    if (Array.isArray(data.customCategories)) setCustomCategories(data.customCategories);
    if (Array.isArray(data.categoryOrder)) setCategoryOrder(data.categoryOrder);
    setCategorySeats(data.categorySeats || {});
    setCategoryLabels(data.categoryLabels || {});
    _lastFetched = Date.now();
    // Tell the screen to look again. Without this the rules change underneath a page
    // that has already rendered and nothing moves until something else re-renders it.
    notifyCategoryConfigChanged();
  } catch (err) {
    console.error("[carCategory] could not load the admin category config:", err);
  }
}
